// CHG-1277 / ITER-3402 — v2 AcceptInvitePage.
//
// Handles {dashboard_url}/accept-invite?token=<X>. Backend contract:
//   GET  /auth/invite-status?token=X   → invite metadata (org name, role, email)
//   POST /accept-invite { token, password } → completes acceptance
//   (CHG-2956 / program Slice I — the backend field is `password`; the
//   pre-fix `new_password` 422-ed every v2 acceptance. invite-status
//   returns `organization_name`; role_name does not exist on the wire.)
//
// v1 implementation (web/dashboard/src/pages/auth/AcceptInvitePage.tsx)
// has a multi-state token machine + brand-isolation logic (CHG-0474,
// CHG-1048) and writes auth tokens via setAccessToken/setRefreshToken/
// setStoredAccountEnvelope to localStorage. Those auth-storage hooks
// are part of the CHG-1167 forward-promise; v2 here implements the
// minimal viable port — accept the invite + show success; leave
// session establishment to the subsequent /login flow.

import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiClient } from "../../api/client";
import { AuthLayout } from "./AuthLayout";
import { usePasswordPolicy } from "./usePasswordPolicy";
import { inputStyle, labelStyle, errorStyle, submitStyle, ctaLinkStyle } from "./styles";

interface InviteStatus {
  // CHG-2964 / DRIFT-1153 branch 2 — GET /auth/invite-status returns a 200
  // {status} envelope (pending|accepted|expired|invalid) for EVERY token,
  // including dead ones. The carrier must branch the rendered UI on this
  // field instead of showing a submittable password form for every 200.
  status?: "pending" | "accepted" | "expired" | "invalid";
  email?: string;
  organization_name?: string;
}

type State =
  | "loading"
  | "ready"
  | "submitting"
  | "success"
  // CHG-2964 / DRIFT-1153 branch 2 — terminal dead-end states for the
  // non-pending invite-status envelopes (no submittable form).
  | "accepted"
  | "expired"
  | "invalid"
  | "error";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [status, setStatus] = useState<InviteStatus | null>(null);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState<State>("loading");
  const [error, setError] = useState<string>("");
  // CHG-2545 / DRIFT-934 — drive the min-length guard from the live
  // server policy (org-configurable, default 12) instead of a hardcoded 8.
  const { policy } = usePasswordPolicy();
  const minLength = policy.min_length;

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        if (!cancelled) {
          setError("Missing invite token. Use the link from your invitation email.");
          setState("error");
        }
        return;
      }
      try {
        const res = await apiClient().get("/auth/invite-status", { params: { token } });
        if (!cancelled) {
          const data = (res.data ?? {}) as InviteStatus;
          setStatus(data);
          // CHG-2964 / DRIFT-1153 branch 2 — branch on the invite-status
          // envelope (the generated twin rendered the password form for
          // EVERY 200, so dead/used/invalid tokens presented a submittable
          // form that only failed at POST). Mirrors v1 AcceptInvitePage
          // (BUG-877 / CHG-0474). Unknown/missing status → invalid (safe
          // dead-end, never a form), matching v1's switch default.
          switch (data.status) {
            case "pending":
              setState("ready");
              break;
            case "accepted":
              setState("accepted");
              break;
            case "expired":
              setState("expired");
              break;
            case "invalid":
            default:
              setState("invalid");
              break;
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ??
              "This invitation link is no longer valid. Ask your administrator for a new invite.",
          );
          setState("error");
        }
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (pwd.length < minLength) {
      setError(`Password must be at least ${minLength} characters.`);
      return;
    }
    if (pwd !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setState("submitting");
    try {
      // CHG-2958 (Slice J) — corrected endpoint: the auth router mounts at
      // /api/v1/auth/*; the previous "/accept-invite" path 404'd.
      await apiClient().post("/auth/accept-invite", { token, password: pwd });
      setState("success");
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "Failed to accept invitation. Please try again.");
      setState("ready");
    }
  }

  if (state === "loading") {
    return (
      <AuthLayout pageId="accept-invite" title="Loading invitation…">
        <div data-role="spinner" style={{ textAlign: "center", padding: 16, color: "var(--color-text-muted)", fontSize: 13 }}>
          Checking your invitation…
        </div>
      </AuthLayout>
    );
  }

  if (state === "error") {
    return (
      <AuthLayout pageId="accept-invite" title="Invitation unavailable">
        <div data-role="error" style={errorStyle}>
          {error}
        </div>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Go to sign-in
        </Link>
      </AuthLayout>
    );
  }

  // CHG-2964 / DRIFT-1153 branch 2 — terminal dead-end states for the
  // non-pending invite-status envelopes. None of these render a password
  // form (the generated twin's defect); each gives an actionable CTA.
  if (state === "accepted") {
    const signInHref = status?.email
      ? `/login?email=${encodeURIComponent(status.email)}`
      : "/login";
    return (
      <AuthLayout pageId="accept-invite" title="Invitation already accepted">
        <p data-role="invite-accepted" style={{ fontSize: 14, color: "var(--color-text)", textAlign: "center" }}>
          This invitation has already been used to set up an account
          {status?.email ? ` for ${status.email}` : ""}. Please sign in instead.
        </p>
        <Link to={signInHref} data-action="goto-login" style={ctaLinkStyle()}>
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (state === "expired") {
    return (
      <AuthLayout pageId="accept-invite" title="Invitation expired">
        <p data-role="invite-expired" style={{ fontSize: 14, color: "var(--color-text)", textAlign: "center" }}>
          This invitation link has expired. Please ask your administrator to send you a new one.
        </p>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (state === "invalid") {
    return (
      <AuthLayout pageId="accept-invite" title="Invalid invitation">
        <p data-role="invite-invalid" style={{ fontSize: 14, color: "var(--color-text)", textAlign: "center" }}>
          This invitation link is no longer valid. Please check your email for the correct link,
          or ask your administrator to resend the invitation.
        </p>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (state === "success") {
    return (
      <AuthLayout pageId="accept-invite" title="Invitation accepted">
        <p style={{ fontSize: 14, color: "var(--color-text)", textAlign: "center" }}>
          Welcome{status?.organization_name ? ` to ${status.organization_name}` : ""}. You can now sign in to access
          your new account.
        </p>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Continue to sign-in
        </Link>
      </AuthLayout>
    );
  }

  const subtitle = status?.organization_name
    ? `Join ${status.organization_name}`
    : undefined;

  return (
    <AuthLayout
      pageId="accept-invite"
      title="Accept your invitation"
      subtitle={subtitle}
    >
      <form onSubmit={onSubmit} data-form="accept-invite">
        {status?.email && (
          <div
            data-role="invite-email"
            style={{
              padding: "8px 10px",
              fontSize: 13,
              background: "var(--color-bg-subtle)",
              borderRadius: "var(--radius-sm, 6px)",
              marginBottom: 12,
            }}
          >
            Invited as <strong>{status.email}</strong>
          </div>
        )}
        <label style={labelStyle}>
          Create a password
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={minLength}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            disabled={state === "submitting"}
            data-field="new-password"
            style={inputStyle}
          />
        </label>
        <label style={{ ...labelStyle, marginTop: 12 }}>
          Confirm password
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={state === "submitting"}
            data-field="confirm-password"
            style={inputStyle}
          />
        </label>
        {error && (
          <div data-role="error" style={errorStyle}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={state === "submitting"}
          data-action="submit"
          style={submitStyle(state === "submitting")}
        >
          {state === "submitting" ? "Accepting…" : "Accept invitation"}
        </button>
      </form>
    </AuthLayout>
  );
}
